import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  getAuditQueueUseCases,
  getAuditQueueTasks,
  getAuditQueueResults,
  setCurrentUser,
  AuditQueueUseCase,
  AuditQueueTask,
  AuditQueueResult,
  AuditQueueUser,
} from "src/store/auditQueueSlice";

export interface UseAuditQueueReturn {
  useCases: AuditQueueUseCase[];
  tasks: AuditQueueTask[];
  results: AuditQueueResult[];
  currentUser: AuditQueueUser;
  isLoading: boolean;
  isError: boolean;
  useCaseById: (id: string) => AuditQueueUseCase | undefined;
  resultsForTask: (taskId: string) => AuditQueueResult[];
  resultForCurrentUser: (taskId: string) => AuditQueueResult | undefined;
}

export function useAuditQueue(): UseAuditQueueReturn {
  const dispatch = useAppDispatch();

  const {
    useCases,
    tasks,
    results,
    currentUser,
    getUseCasesLoading,
    getTasksLoading,
    getResultsLoading,
  } = useAppSelector((state) => state.auditQueueSlice);

  // Read the logged-in user from the existing userSlice and push into auditQueueSlice.
  const { alias, userLoading } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (alias && alias !== currentUser.login) {
      dispatch(
        setCurrentUser({
          login: alias,
          name: alias,
          role: "auditor",
          isElevated: false,
        }),
      );
    }
  }, [alias, currentUser.login, dispatch]);

  // Fetch data once the logged-in alias is known.
  useEffect(() => {
    if (!alias) return;
    if (getUseCasesLoading === "idle") dispatch(getAuditQueueUseCases());
    if (getTasksLoading === "idle") dispatch(getAuditQueueTasks(alias));
  }, [dispatch, alias, getUseCasesLoading, getTasksLoading]);

  // Fetch results once tasks are loaded.
  useEffect(() => {
    if (!alias) return;
    if (getTasksLoading === "fulfilled" && getResultsLoading === "idle") {
      const taskIds = tasks.map((t) => t.id);
      dispatch(getAuditQueueResults({ taskIds, currentAlias: alias }));
    }
  }, [dispatch, alias, getTasksLoading, getResultsLoading, tasks]);

  // Treat "idle" as loading too — thunks dispatch in useEffect (after first
  // render), so without this the component briefly flashes the empty state.
  // Once the user slice has resolved and alias is still empty, stop waiting
  // so we do not show a spinner forever.
  const aliasResolved = userLoading === "fulfilled" || userLoading === "rejected";
  const isLoading = !aliasResolved
    ? true
    : getUseCasesLoading === "idle" ||
      getUseCasesLoading === "pending" ||
      getTasksLoading === "idle" ||
      getTasksLoading === "pending" ||
      getResultsLoading === "idle" ||
      getResultsLoading === "pending";

  const isError =
    (aliasResolved && !alias) ||
    getUseCasesLoading === "rejected" ||
    getTasksLoading === "rejected" ||
    getResultsLoading === "rejected";

  // Selectors (memoised so child components see stable references).
  const useCaseById = useCallback((id: string) => useCases.find((u) => u.id === id), [useCases]);

  const resultsForTask = useCallback(
    (taskId: string) => results.filter((r) => r.task_id === taskId),
    [results],
  );

  const resultForCurrentUser = useCallback(
    (taskId: string) =>
      results.find((r) => r.task_id === taskId && r.auditor_id === currentUser.login),
    [results, currentUser.login],
  );

  return {
    useCases,
    tasks,
    results,
    currentUser,
    isLoading,
    isError,
    useCaseById,
    resultsForTask,
    resultForCurrentUser,
  };
}
